import { Alert, AlertProps, AlertTitle, Typography } from '@mui/material'
import { ActionRequest, AlertNotified, Device, Notification, RuuviTag } from '../types'
import { useEffect, useState } from 'react'
import { getRequests } from '../services/requestService'
import { RequestableType } from '../enums/RequestableType'
import { RequestStatus } from '../enums/RequestStatus'
import { ActionRequestAction } from '../enums/ActionRequestAction'
import ActionRequestAcceptButton from '../components/ActionRequestAcceptButton'
import { useTranslation } from 'react-i18next'
import { subscribeActionRequestNotifications } from '../subscribers'
import { EntityEventAction } from '../enums'
import { isActionRequestClosed } from '../utils/actionRequest'
import { subHours } from 'date-fns'
import { clearAlertNotified, getAlertNotified } from '../services/alertService'
import { AlertSensor } from '../types/Alerts'
import { LoadingButton } from '@mui/lab'

export interface EnableAlertNotificationsAlertProps extends Omit<
  AlertProps,
  'children' | 'severity' | 'action'
> {
  device?: Device
  ruuviTag?: RuuviTag
  afterAccept?: (actionRequest?: ActionRequest) => void
}

export default function EnableAlertNotificationsAlert({
  device,
  ruuviTag,
  afterAccept,
  ...props
}: EnableAlertNotificationsAlertProps): JSX.Element {
  /**
   * The translate function.
   */
  const { t } = useTranslation('actionRequest')

  /**
   * The action requests state.
   */
  const [actionRequests, setActionRequests] = useState<ActionRequest[]>([])

  /**
   * The alert notified state.
   */
  const [alertNotified, setAlertNotified] = useState<AlertNotified>()

  /**
   * The submitting state.
   */
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  /**
   * The action request.
   */
  const actionRequest = actionRequests.length > 0 ? actionRequests[0] : undefined

  /**
   * The loading state.
   */
  const [_loading, setLoading] = useState<boolean>(true)

  /**
   * The error state.
   */
  const [_error, setError] = useState<unknown>()

  /**
   * Load the alert notified state by device.
   */
  async function loadAlertNotifiedByDevice(deviceName: string, signal: AbortSignal) {
    setAlertNotified(
      await getAlertNotified({ type: AlertSensor.DEVICE, device: deviceName }, { signal })
    )
  }

  /**
   * Load the alert notified state by ruuvitag.
   */
  async function loadAlertNotifiedByRuuviTag(ruuviTagId: string, signal: AbortSignal) {
    setAlertNotified(
      await getAlertNotified({ type: AlertSensor.RUUVITAG, device: ruuviTagId }, { signal })
    )
  }

  /**
   * Load the device action requests.
   */
  async function loadDeviceActionRequests(deviceName: string, signal: AbortSignal) {
    try {
      setLoading(true)

      let actionRequests = await getRequests(
        {
          requestableType: RequestableType.DEVICE,
          requestableId: deviceName,
          action: ActionRequestAction.ENABLE_ALERT_NOTIFICATIONS,
          status: RequestStatus.REQUESTED
        },
        {
          signal
        }
      )

      // Filter action request that are older than 24 hours.
      const now = new Date()
      const twentyFourHoursAgo = subHours(now, 24)
      actionRequests = actionRequests.filter(
        (actionRequest) => new Date(actionRequest.createdAt) >= twentyFourHoursAgo
      )

      setActionRequests(actionRequests)
    } catch (error) {
      setError(error)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Load the ruuvitag action requests.
   */
  async function loadRuuviTagActionRequests(ruuviTagId: string, signal: AbortSignal) {
    try {
      setLoading(true)

      let actionRequests = await getRequests(
        {
          requestableType: RequestableType.RUUVI_TAG,
          requestableId: ruuviTagId,
          action: ActionRequestAction.ENABLE_ALERT_NOTIFICATIONS,
          status: RequestStatus.REQUESTED
        },
        {
          signal
        }
      )

      // Filter action request that are older than 24 hours.
      const now = new Date()
      const twentyFourHoursAgo = subHours(now, 24)
      actionRequests = actionRequests.filter(
        (actionRequest) => new Date(actionRequest.createdAt) >= twentyFourHoursAgo
      )

      setActionRequests(actionRequests)
    } catch (error) {
      setError(error)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Remove the action request.
   */
  function removeActionRequest(actionRequest: ActionRequest) {
    setActionRequests((prev) => prev.filter((a) => a.id !== actionRequest.id))
  }

  /**
   * Handle the "after submit" action.
   */
  function handleNotification(notification: Notification<ActionRequest>) {
    const actionRequest = notification.body.data

    const isThisDeviceRequest =
      device &&
      actionRequest.requestableType === RequestableType.DEVICE &&
      actionRequest.requestableId === device?.name

    const isThisRuuviTagRequest =
      ruuviTag &&
      actionRequest.requestableType === RequestableType.RUUVI_TAG &&
      actionRequest.requestableId === ruuviTag.id

    if (!isThisDeviceRequest && !isThisRuuviTagRequest) {
      return
    }

    if (
      notification.action === EntityEventAction.RemoveActionRequest ||
      notification.action === EntityEventAction.SoftRemoveActionRequest ||
      (notification.action === EntityEventAction.UpdateActionRequest &&
        isActionRequestClosed(actionRequest))
    ) {
      // Remove the action request.
      removeActionRequest(actionRequest)
    }
  }

  /**
   * Subscribe to action request notifications.
   */

  subscribeActionRequestNotifications(handleNotification)

  /**
   * Load the action requests.
   */
  useEffect(() => {
    const controller = new AbortController()
    if (device?.name) {
      loadAlertNotifiedByDevice(device.name, controller.signal)
      loadDeviceActionRequests(device.name, controller.signal)
    } else if (ruuviTag?.id) {
      loadAlertNotifiedByRuuviTag(ruuviTag.id, controller.signal)
      loadRuuviTagActionRequests(ruuviTag.id, controller.signal)
    }
    return () => controller.abort()
  }, [device?.name, ruuviTag?.id])

  /**
   * Poll the alert notified state by device.
   */
  useEffect(() => {
    if (device?.name) {
      const controller = new AbortController()

      const ONE_MINUTE = 60 * 1000
      const interval = setInterval(() => {
        loadAlertNotifiedByDevice(device.name, controller.signal)
      }, ONE_MINUTE)

      return () => {
        controller.abort()
        clearInterval(interval)
      }
    }
  }, [device?.name])

  /**
   * Poll the alert notified state by ruuvitag.
   */
  useEffect(() => {
    if (ruuviTag?.id) {
      const controller = new AbortController()

      const ONE_MINUTE = 60 * 1000
      const interval = setInterval(() => {
        loadAlertNotifiedByRuuviTag(ruuviTag.id, controller.signal)
      }, ONE_MINUTE)

      return () => {
        controller.abort()
        clearInterval(interval)
      }
    }
  }, [ruuviTag?.id])

  /**
   * Handle the submit action.
   */
  async function handleSubmit() {
    try {
      setSubmitting(true)

      if (device?.name) {
        await clearAlertNotified({ type: AlertSensor.DEVICE, device: device.name })
      } else if (ruuviTag?.id) {
        await clearAlertNotified({ type: AlertSensor.RUUVITAG, device: ruuviTag.id })
      }

      // Clear the alert notified state.
      setAlertNotified(undefined)
    } catch {
      // Ignore the errors for now.
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * Handle the after submit event.
   */
  function handleAfterSubmit(actionRequest?: ActionRequest) {
    setAlertNotified(undefined)
    if (afterAccept) {
      afterAccept(actionRequest)
    }
  }

  if ((!alertNotified || !alertNotified.notified) && !actionRequest) {
    return <></>
  }

  return (
    <>
      {actionRequest ? (
        <Alert severity="warning" {...props}>
          <AlertTitle>
            {t('EnableAlertNotifications.title', {
              device: device
                ? (device?.asset?.name ?? device.name)
                : ruuviTag
                  ? (ruuviTag?.asset?.name ?? ruuviTag?.id)
                  : '-'
            })}
          </AlertTitle>

          <Typography>{t('EnableAlertNotifications.message')}</Typography>

          <ActionRequestAcceptButton
            actionRequest={actionRequest}
            label={t('EnableAlertNotifications.action')}
            size="small"
            variant="contained"
            color="warning"
            sx={{ mt: 2 }}
            afterSubmit={handleAfterSubmit}
          />
        </Alert>
      ) : (
        <Alert severity="warning" {...props}>
          <AlertTitle>{t('enableAlertNotifications')}</AlertTitle>

          <Typography>{t('EnableAlertNotifications.message')}</Typography>

          <LoadingButton
            size="small"
            variant="contained"
            color="warning"
            loading={isSubmitting}
            sx={{ mt: 2 }}
            onClick={handleSubmit}
          >
            {t('EnableAlertNotifications.action')}
          </LoadingButton>
        </Alert>
      )}
    </>
  )
}
